<template>
  <card>
    <v-row no-gutters class="sv-node-editor-header align-start">
      <v-col class="py-2 flex-grow-0">
        <language-select
          :langs="nodeLangs"
          :lang="currentLang"
          @before-change-lang="beforeChangeLang"
        />
      </v-col>
      <v-spacer />
      <v-col v-if="meta.class.can_publish" class="flex-grow-0">
        <validation-provider
          mode="eager"
          vid="vid_published"
          name="Опубликовано"
        >
          <v-checkbox
            v-model="nodeData.published[nodeLang]"
            inset
            :false-value="0"
            :true-value="1"
            class="pl-4 pt-0 my-2"
            :readonly="readonly"
            label="Опубликовано"
            hide-details
          />
        </validation-provider>
      </v-col>
      <v-col v-if="wwwPath.length > 0" class="flex-grow-0">
        <action-col-btn
          id="btn-www"
          tooltip="Просмотр на сайте"
          :href="wwwPath"
          target="checking-www"
          class="check-www-button"
        >
          mdi-monitor
        </action-col-btn>
      </v-col>
    </v-row>

    <v-tabs-items
      v-model="currentLang"
      v-resize="onResize"
      :style="getStyle"
      class="overflow-y-auto"
    >
      <v-tab-item
        v-for="lang in nodeLangs"
        :key="lang"
        :value="lang"
        :transition="false"
        eager
        class="pt-5"
      >
        <card-content class="pt-0">
          <validation-observer :ref="`validator${lang}`" tag="div">
            <node-form
              v-model="nodeData"
              :meta="meta"
              :action="formAction"
              :size="size"
              :readonly="readonly"
              :can-sort="canSort"
              :node-lang="lang"
              :refs-dic="refsDic"
              :mirrors-dic="mirrorsDic"
              @title-change="onTitleChange"
            />
          </validation-observer>
        </card-content>
      </v-tab-item>
    </v-tabs-items>

    <validation-observer ref="validatorbase" tag="div">
      <card-actions>
        <div class="mr-4 col-4 pa-0">
          <validation-provider
            v-slot="{ errors }"
            rules="required|regex_slug|max:50"
            mode="eager"
            name="Идентификатор"
          >
            <v-text-field
              :id="`form-text-name`"
              v-model="nodeData.name"
              label="Идентификатор"
              outlined
              dense
              maxlength="50"
              :readonly="readonly"
              :error-messages="errors"
              hide-details
              @input="nameDirty = true"
            >
              <template v-slot:append-outer>
                <v-tooltip v-if="errors.length > 0" top color="error">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="error" dark v-bind="attrs" v-on="on">
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ errors.join("\n") }}</span>
                </v-tooltip>
                <div v-else class="mr-6"></div>
              </template>
            </v-text-field>
          </validation-provider>
        </div>
        <div v-if="canSort" class="col-2 pa-0">
          <validation-provider
            v-slot="{ errors }"
            rules="required|is_integer"
            mode="eager"
            name="Порядок"
          >
            <v-text-field
              :id="`form-text-seq`"
              v-model="nodeData.seq"
              label="Порядок"
              outlined
              dense
              :error-messages="errors"
              hide-details
              :readonly="readonly"
            >
              <template v-slot:append-outer>
                <v-tooltip v-if="errors.length > 0" top color="error">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="error" dark v-bind="attrs" v-on="on">
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ errors.join("\n") }}</span>
                </v-tooltip>
                <div v-else class="mr-6"></div>
              </template>
            </v-text-field>
          </validation-provider>
        </div>

        <v-spacer />
        <v-btn
          v-if="canClose"
          id="form-btn-cancel"
          color="primary"
          depressed
          outlined
          @click="$emit('editor-close')"
        >
          {{ readonly ? "Закрыть" : "Отменить" }}
        </v-btn>
        <v-btn
          v-if="!readonly"
          id="form-btn-save"
          color="primary"
          depressed
          outlined
          @click="saveNode(false)"
        >
          Сохранить
        </v-btn>
        <v-btn
          v-if="!readonly && canClose"
          id="form-btn-save"
          color="primary"
          depressed
          @click="saveNode(true)"
        >
          Сохранить и закрыть
        </v-btn>
      </card-actions>
    </validation-observer>
  </card>
</template>
<script>
import CheckPermissionMixin from "@/mixins/CheckPermissionMixin";
import NotificationMixin from "@/mixins/NotificationMixin";
import ResizeMixin from "@/mixins/ResizeMixin";
import Repository from "@/api/node";
import NodeForm from "./NodeForm.vue";
import LanguageSelect from "@/components/LanguageSelect";
import Translit from "@/utils/Translit.js";
import { getEmptyByTag } from "@/utils/EnvHelper.js";
import { cloneDeep } from "lodash";

export default {
  components: { NodeForm, LanguageSelect },

  mixins: [NotificationMixin, CheckPermissionMixin, ResizeMixin],

  props: {
    meta: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      required: true
    },
    size: {
      type: String,
      default: "lg"
    },
    nodeId: {
      type: [String, Number],
      default: 0
    },
    parentNodeId: {
      type: [String, Number],
      default: 0
    },
    parentClassId: {
      type: [String, Number],
      default: 0
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    canClose: {
      type: Boolean,
      default: true
    },
    canSort: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      currentLang: "",
      nameInDb: "",
      nameDirty: false,
      nodeData: {
        published: {},
        name: "",
        seq: "",
        fields: {},
        refs: {},
        mirrors: []
      },
      refsDic: {},
      mirrorsDic: []
    };
  },

  computed: {
    getStyle() {
      if (this.autoHeight) {
        return { height: this.height(226) };
      } else {
        return {};
      }
    },
    nodeLangs() {
      return this.$store.getters.getLangs;
    },
    nodeLang() {
      return this.$store.getters.getLang;
    },
    formAction() {
      if (this.readonly) {
        return "view";
      }
      return this.nodeId ? "edit" : "create";
    },
    langsFill() {
      return value =>
        this.nodeLangs.reduce((p, i) => {
          p[i] = cloneDeep(value);
          return p;
        }, {});
    },
    nodeDataComputed() {
      return this.$store.getters.getCurrentNode(this.nodeId);
    },
    wwwPath() {
      if (this.nodeId == 0) return "";

      const refs =
        this.$store.getters.getMetaChildren(this.parentClassId) || [];
      const classMeta = this.meta.class;
      const linkData = refs.find(ref => ref.class_id_2 == classMeta.id);
      const L = this.nodeDataComputed.p.length - 1;
      const www_path = this.nodeDataComputed.p.reduce((path, entry, i) => {
        const ind = L - i;
        return path.replace(`{node_name:p${ind}}`, entry.pn);
      }, linkData?.custom_settings?.www_path || classMeta?.custom_settings?.www_path || "");
      return www_path
        .replace("{node_name}", this.nameInDb)
        .replace("{id}", this.nodeId);
    }
  },

  created() {
    this.currentLang = this.nodeLang;
    this.resetNode();
  },

  mounted() {
    this.reload();
  },

  methods: {
    reload() {
      this.currentLang = this.nodeLang;
      this.resetNode();
      if (this.nodeId != 0) {
        this.loadNode();
      } else {
        this.loadCreateTemplate();
      }
    },

    async checkValid(jumpToInvalid) {
      let lastTabError = "";
      let valid = this.$refs.validatorbase
        ? await this.$refs.validatorbase.validate()
        : true;

      if (valid) {
        for (const nodeLang of this.nodeLangs) {
          if (!(await this.checkValidLang(nodeLang))) {
            valid = false;
            lastTabError = nodeLang;
            break;
          }
        }
      }

      if (!valid) {
        if (jumpToInvalid && lastTabError.length > 0) {
          this.currentLang = lastTabError;
        }
      }

      return valid;
    },

    async checkValidLang(nodeLang) {
      const validator = this.$refs[`validator${nodeLang}`];
      if (this.nodeData.published[nodeLang] == 1 && validator) {
        return await validator[0].validate();
      } else {
        return true;
      }
    },

    async saveNode(close) {
      try {
        if (!(await this.checkValid(true))) return false;

        const { nodeData, nodeId } = this;
        const payload = { ...nodeData };

        this.loading = true;

        const { data } =
          nodeId > 0
            ? await Repository.update(nodeId, payload)
            : await Repository.create(
                this.parentNodeId,
                this.meta.class.id,
                payload
              );

        if (close) {
          this.$emit("apply");
        } else {
          this.nameInDb = this.nodeData.name;
          this.$store.dispatch("fetchCurrentNodeForce", nodeId || data.id);
        }

        // это надо как минимум, чтобы обновились все словари
        this.$store.dispatch("fetchProfile");
        this.sendAlert(data.alert);

        if (nodeId == 0 && !close && data.id) {
          this.$router.push({ name: "Node", params: { id: data.id } });
        }
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },
    async loadNode() {
      try {
        const { nodeId } = this;
        this.loading = true;
        const { data } = await Repository.getOne(nodeId);
        this.refsDic = data.refsDic;
        this.mirrorsDic = data.mirrorsDic;
        this.$set(this.nodeData, "published", data.published);
        this.$set(this.nodeData, "seq", data.seq);
        this.$set(this.nodeData, "name", data.name);
        this.nameInDb = data.name;
        Object.entries(data.fields).forEach(entry => {
          this.$set(this.nodeData.fields, entry[0], entry[1]);
        });
        Object.entries(data.refs).forEach(entry => {
          this.$set(this.nodeData.refs, entry[0], entry[1]);
        });
        this.$set(this.nodeData, "mirrors", data.mirrors);
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },

    async loadCreateTemplate() {
      try {
        this.loading = true;
        const { data } = await Repository.getCreateTemplate(
          this.parentNodeId,
          this.meta.class.id
        );
        this.$set(this.nodeData, "seq", data.seq);
        this.refsDic = data.refsDic;
        this.mirrorsDic = data.mirrorsDic;
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },

    resetNode() {
      const nodeData = {
        published: this.langsFill(0),
        name: "",
        fields: this.meta.tags.reduce((p, i) => {
          const value = getEmptyByTag(i.typ);
          p[i.id] = i.multilang == 1 ? this.langsFill(value) : value;
          return p;
        }, {}),
        refs: Object.keys(this.meta.references).reduce((p, groupId) => {
          const group = this.$store.getters.getMetaLinkGroup(groupId);
          p[groupId] = group.multilang == 1 ? this.langsFill([]) : [];
          return p;
        }, {}),
        mirrors: []
      };
      nodeData.published[this.currentLang] = 1;
      this.nameInDb = "";
      this.nodeData = nodeData;
      this.nodeLangs.forEach(lang => {
        const validator = this.$refs[`validator${lang}`];
        if (validator?.length > 0) {
          validator[0].reset();
        }
      });
    },
    beforeChangeLang(lang) {
      this.currentLang = lang;
    },
    onTitleChange(val) {
      if (this.nodeId == 0 && !this.nameDirty) {
        // if adding & not edited name
        this.nodeData.name = Translit(val).substring(0, 50);
      }
    }
  }
};
</script>
<style lang="scss">
@import "@/styles/branding.scss";

.sv-node-editor-header {
  padding: 8px 16px !important;
  border-bottom: 1px solid $brand-border-table-inner !important;
}
.check-www-button {
  padding-top: 6px;
  padding-bottom: 6px;
  margin-left: 12px;
}
</style>
